import { useContext, useLayoutEffect, useRef } from 'react'

import { getChannelLabel } from '@new/domains/operations/utils/stayInbox'

import AuthContext from '@contexts/Auth/AuthContext'

import { AutoCard } from './components/AutoCard'
import { CallCard } from './components/CallCard'
import { CaseNote } from './components/CaseNote'
import { CaseOpenedCard } from './components/CaseOpenedCard'
import { ConflictCard } from './components/ConflictCard'
import { HandledStrip } from './components/HandledStrip'
import { DayHeading, SystemLine } from './components/Shared.styles'
import { SourceGapNotice } from './components/SourceGapNotice'
import { TimelineEvent } from './components/TimelineEvent'
import { buildTimelineItems } from './helpers/buildTimelineItems'
import { findAutomatedSend } from './helpers/findAutomatedSend'
import { getRunFlags, TimelineItem } from './StayTimeline.items'
import * as S from './StayTimeline.styles'
import {
  GuestInboxStayFacts,
  GuestInboxTimelineConversation,
  GuestInboxTimelineEvent,
} from './StayTimeline.types'
import {
  formatHours,
  getClippedSpoke,
  groupEventsByDay,
  hasSourceGap,
  toStayDetail,
} from './StayTimeline.utils'
import { useStayTimelineLive } from './useStayTimelineLive'

export interface StayTimelineProps {
  /** The conversation payload: events, calls, cases, sends. */
  thread: GuestInboxTimelineConversation
  /** Guest inbox facts; absent for historical bookings, which then get the plain conversation. */
  facts?: GuestInboxStayFacts
  /** Guest-facing events of the thread. */
  conversation: GuestInboxTimelineEvent[]
  /** Events the timeline lists, as the filters narrow them. */
  events: GuestInboxTimelineEvent[]
  /** Whether the timeline is shown; while it is, the latest message is kept scrolled into view. */
  isVisible?: boolean
  isFiltered: boolean
  isUpdatingFilter?: boolean
  onClearFilters: () => void
  onUpdateNote?: (noteId: string, body: string) => Promise<boolean>
  onDeleteNote?: (noteId: string) => Promise<boolean>
}

export const StayTimeline = ({
  thread,
  facts,
  conversation,
  events,
  isVisible = true,
  isFiltered,
  isUpdatingFilter = false,
  onClearFilters,
  onUpdateNote,
  onDeleteNote,
}: StayTimelineProps) => {
  useStayTimelineLive({ bookingId: thread.booking?.id, isVisible })

  const { user } = useContext(AuthContext)

  const endRef = useRef<HTMLDivElement>(null)
  const { calls, caseList, spokeThread, automatedSends } = thread
  const detail = facts ? toStayDetail({ facts, thread }) : undefined
  const readiness = facts?.readiness
  const stay = facts?.stay
  const guest = facts?.guest

  const gap = facts
    ? hasSourceGap({
        conversation,
        callsCount: calls.length,
        spokeOutbound: facts.spoke.outbound,
        casesTotal: facts.cases.total,
        sendsTotal: facts.sends.total,
      })
    : false
  const clipped = getClippedSpoke({ spokeTotal: spokeThread?.total, conversation })

  const items = buildTimelineItems({
    conversation: events,
    calls,
    caseList,
    guestName: guest?.name,
  })
  const runFlags = getRunFlags(items)
  const flagsByKey = new Map(items.map((item, index) => [item.key, runFlags[index]] as const))
  const groups = groupEventsByDay(items)

  const isLate = readiness?.leadHours != null && readiness.leadHours < 0
  const Marker = isLate ? S.CleanMarkerLate : S.CleanMarker

  useLayoutEffect(
    function scrollToLatestMessage() {
      if (isVisible) endRef.current?.scrollIntoView({ block: 'end' })
    },
    [isVisible, items.length],
  )

  const renderItem = (item: TimelineItem) => {
    const flags = flagsByKey.get(item.key) ?? { isFirst: true, isLast: true }

    if (item.kind === 'caseOpened') {
      return item.caseItem ? (
        <CaseOpenedCard
          key={item.key}
          caseItem={item.caseItem}
          checkIn={stay?.checkIn}
          checkout={stay?.checkout}
        />
      ) : null
    }

    if (!item.event) return null

    if (item.kind === 'call') {
      return item.call ? (
        <CallCard
          key={item.key}
          call={item.call}
          at={item.at}
          checkIn={stay?.checkIn}
          checkout={stay?.checkout}
          guestName={guest?.name}
        />
      ) : null
    }

    if (item.kind === 'caseNote') return <CaseNote key={item.key} event={item.event} />
    if (item.kind === 'auto') {
      return (
        <AutoCard
          key={item.key}
          event={item.event}
          send={findAutomatedSend({ eventId: item.event.id, sends: automatedSends })}
          checkIn={stay?.checkIn}
          checkout={stay?.checkout}
        />
      )
    }
    if (item.kind === 'sys') return <SystemLine key={item.key}>{item.event.body}</SystemLine>

    const { noteId, authorId } = item.event
    const isOwnNote = !!authorId && authorId === user?.voyageId
    const isAllowedToChangeNote = item.kind === 'note' && noteId && isOwnNote
    const noteActions =
      isAllowedToChangeNote && onUpdateNote && onDeleteNote
        ? {
            onUpdate: (body: string) => onUpdateNote(noteId, body),
            onDelete: () => onDeleteNote(noteId),
          }
        : undefined

    return (
      <TimelineEvent
        key={item.key}
        event={item.event}
        variant={item.kind}
        flags={flags}
        guestName={guest?.name}
        checkIn={stay?.checkIn}
        checkout={stay?.checkout}
        noteActions={noteActions}
      />
    )
  }

  const renderMessages = () => {
    if (items.length) {
      return groups.map(group => (
        <div key={group.key}>
          <DayHeading>{group.label}</DayHeading>
          {group.events.map(renderItem)}
        </div>
      ))
    }

    if (!isFiltered) {
      return (
        <S.FilteredEmpty
          title="No conversation recorded on this stay."
          description="Messages and calls with the guest will show up here as they happen."
        />
      )
    }

    return (
      <S.FilteredEmpty
        title="No messages with this filter."
        description="Try other filters, or go back to the default ones."
        showAction
        actionTitle="Reset filters"
        onClick={onClearFilters}
      />
    )
  }

  return (
    <>
      {facts?.judgement.conflict ? (
        <ConflictCard
          conflict={facts.judgement.conflict}
          topCase={facts.cases.top}
          conversation={conversation}
        />
      ) : null}

      {detail ? <HandledStrip detail={detail} conversation={conversation} /> : null}

      {readiness?.cleanDoneLocal ? (
        <Marker>
          Home cleaned {readiness.cleanDoneLocal}
          {readiness.leadHours != null
            ? ` — ${formatHours(Math.abs(readiness.leadHours))} ${isLate ? 'after' : 'before'} check-in`
            : ''}
        </Marker>
      ) : null}

      {facts ? (
        <SourceGapNotice
          bookingCode={facts.code}
          hasGap={gap}
          clipped={clipped}
          channelLabel={getChannelLabel(facts.channel)}
        />
      ) : null}

      <S.Messages $isUpdating={isUpdatingFilter}>{renderMessages()}</S.Messages>

      <div ref={endRef} />
    </>
  )
}
